import React from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { SmartHeader, SmartField, NoRecordsCard } from './_SmartFieldRenderer';

const Section = ({ icon, title, children }) => (
    <div className="pt-3 pb-3 border-bottom-1 surface-border">
        <div className="flex align-items-center gap-2 mb-3">
            <i className={`pi ${icon} text-primary`} aria-hidden="true" />
            <span className="text-sm font-semibold text-900 uppercase">{title}</span>
        </div>
        <div className="grid">{children}</div>
    </div>
);

const formatDate = (s) => {
    if (!s) return null;
    try {
        const d = new Date(typeof s === 'number' ? s : s);
        if (isNaN(d.getTime())) return s;
        return d.toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
        });
    } catch {
        return String(s);
    }
};

const PAN_CATEGORIES = {
    P: 'Individual',
    C: 'Company',
    H: 'HUF (Hindu Undivided Family)',
    F: 'Firm',
    A: 'Association of Persons (AOP)',
    T: 'Trust',
    B: 'Body of Individuals (BOI)',
    L: 'Local Authority',
    J: 'Artificial Juridical Person',
    G: 'Government',
};

const getPANCategory = (c) => PAN_CATEGORIES[c] || c || null;

const formatGender = (g) => {
    if (!g) return null;
    const u = String(g).toUpperCase();
    if (u === 'M' || u === 'MALE') return 'Male';
    if (u === 'F' || u === 'FEMALE') return 'Female';
    return g;
};

// `onDownloadPDF`/`pdfPasswordHint` come from SimpleReportForm, which does the
// authenticated blob download. Shared-link and lead-history views render this
// component without them and simply get no button.
const PAN2OutputComponent = ({ reportData, formData, isSharedView, hideCredits, onDownloadPDF, pdfPasswordHint: pdfPasswordHintProp }) => {
    if (!reportData) return <NoRecordsCard subtitle="No PAN Details data available" />;

    const pdfPasswordHint =
        pdfPasswordHintProp ||
        reportData?.pdf_password_hint ||
        reportData?.data?.pdf_password_hint ||
        reportData?.response?.pdf_password_hint ||
        reportData?.report_json?.pdf_password_hint ||
        null;

    if (reportData?.code === '1004') {
        return <NoRecordsCard subtitle="No credit bureau records found for the provided details. Please enter valid details." />;
    }

    if (reportData?.success === false || reportData?.error) {
        return (
            <Card className="text-center">
                <i className="pi pi-times-circle text-5xl text-red-500 block mb-3" aria-hidden="true" />
                <h3 className="text-lg font-semibold text-900 m-0 mb-2">PAN Verification Failed</h3>
                <p className="text-600 text-sm m-0 mb-3">{reportData?.message || 'Internal server error. Please try again later.'}</p>
                <Button label="Try Again" icon="pi pi-refresh" outlined onClick={() => window.location.reload()} />
            </Card>
        );
    }

    let parsedData = reportData;
    if (typeof reportData === 'string') {
        try { parsedData = JSON.parse(reportData); } catch { /* ignore */ }
    }

    let panRawData = null;
    let responseStatus = null;
    let panDetails = null;

    if (parsedData?.data?.data?.pan_data) {
        panRawData = parsedData.data.data.pan_data;
        responseStatus = parsedData.data.data;
    } else if (parsedData?.report_json?.data?.data?.pan_data) {
        panRawData = parsedData.report_json.data.data.pan_data;
        responseStatus = parsedData.report_json.data.data;
    } else if (parsedData?.data?.raw_response?.data?.data?.pan_data) {
        panRawData = parsedData.data.raw_response.data.data.pan_data;
        responseStatus = parsedData.data.raw_response.data.data;
    } else if (parsedData?.raw_response?.data?.data?.pan_data) {
        panRawData = parsedData.raw_response.data.data.pan_data;
        responseStatus = parsedData.raw_response.data.data;
    } else if (parsedData?.pan_details?.raw_response?.data?.data?.pan_data) {
        panRawData = parsedData.pan_details.raw_response.data.data.pan_data;
        responseStatus = parsedData.pan_details.raw_response.data.data;
    } else if (parsedData?.data) {
        panDetails = parsedData.data;
        responseStatus = parsedData.data;
    } else {
        panDetails = parsedData;
        responseStatus = parsedData;
    }

    if (panRawData) {
        panDetails = {
            name: panRawData.name || null,
            firstName: panRawData.first_name || null,
            middleName: panRawData.middle_name || null,
            lastName: panRawData.last_name || null,
            dob: panRawData.date_of_birth || null,
            gender: panRawData.gender || null,
            pan: panRawData.document_id || panRawData.pan || null,
            typeOfHolder: panRawData.category || null,
            documentType: panRawData.document_type || null,
            email: panRawData.email || null,
            phone: panRawData.phone || null,
            mobile_no: panRawData.mobile_no || null,
            aadhaarSeedingStatus: panRawData.aadhaar_linked || false,
            maskedAadhaar: panRawData.masked_aadhaar_number || null,
            lastFourDigit: panRawData.last_four_digit || null,
            addressData: panRawData.address_data || null,
            code: responseStatus?.code || null,
            message: responseStatus?.message || null,
        };
    } else if (!panDetails && parsedData?.data) {
        panDetails = {
            name: parsedData.data.name || null,
            pan: parsedData.data.pan || null,
            dob: parsedData.data.date_of_birth || null,
            father_name: parsedData.data.father_name || null,
            code: parsedData.data.code || null,
            message: parsedData.data.message || null,
        };
    }

    if (!panDetails) {
        return <NoRecordsCard subtitle="The PAN details data structure is not recognized." />;
    }

    const remainingBalance = reportData?.remaining_balance || reportData?.remaining_credits;

    const aadhaarLinked = panDetails.aadhaarSeedingStatus === true || panDetails.aadhaarSeedingStatus === 'true';
    const addr = panDetails.addressData;

    return (
        <div>
            <SmartHeader title="PAN Verification Report V2" icon="pi-id-card" remainingBalance={remainingBalance} shouldHideCredits={hideCredits || isSharedView} />

            <Card>
                <Section icon="pi-user" title="Personal Details">
                    <SmartField name="full_name" value={panDetails.name} />
                    {panDetails.firstName && <SmartField name="first_name" value={panDetails.firstName} />}
                    {panDetails.middleName && <SmartField name="middle_name" value={panDetails.middleName} />}
                    {panDetails.lastName && <SmartField name="last_name" value={panDetails.lastName} />}
                    {panDetails.dob && <SmartField name="date_of_birth" value={formatDate(panDetails.dob)} />}
                    {panDetails.gender && <SmartField name="gender" value={formatGender(panDetails.gender)} />}
                    {panDetails.typeOfHolder && <SmartField name="holder_type" value={getPANCategory(panDetails.typeOfHolder)} />}
                </Section>

                <Section icon="pi-id-card" title="Identification & Contact">
                    <SmartField name="pan_number" value={panDetails.pan} highlight />
                    <div className="col-12 sm:col-6 lg:col-4 flex flex-column gap-1">
                        <span className="text-500 text-xs font-medium">Aadhaar Status</span>
                        <span>
                            <Tag
                                icon={aadhaarLinked ? 'pi pi-check' : 'pi pi-times'}
                                value={aadhaarLinked ? 'Linked' : 'Not Linked'}
                                severity={aadhaarLinked ? 'success' : 'warning'}
                            />
                        </span>
                    </div>
                    {panDetails.maskedAadhaar && <SmartField name="masked_aadhaar" value={panDetails.maskedAadhaar} />}
                    {panDetails.lastFourDigit && <SmartField name="last_4_digits" value={panDetails.lastFourDigit} />}
                    {panDetails.mobile_no && <SmartField name="mobile_number" value={panDetails.mobile_no} />}
                    {panDetails.email && <SmartField name="email" value={panDetails.email} />}
                </Section>

                {addr && (
                    <Section icon="pi-home" title="Address Information">
                        {addr.line_1 && <SmartField name="address_line_1" value={addr.line_1} />}
                        {addr.line_2 && <SmartField name="address_line_2" value={addr.line_2} />}
                        {addr.street && <SmartField name="street" value={addr.street} />}
                        {addr.city && <SmartField name="city" value={addr.city} />}
                        {addr.line_5 && <SmartField name="area" value={addr.line_5} />}
                        {addr.state && <SmartField name="state" value={addr.state} />}
                        {addr.pincode && <SmartField name="pincode" value={addr.pincode} />}
                    </Section>
                )}

                {onDownloadPDF && (
                    <div className="text-center pt-3">
                        <Button
                            label="Download PAN V2 Report"
                            icon="pi pi-file-pdf"
                            onClick={onDownloadPDF}
                            size="small"
                        />
                        {pdfPasswordHint && (
                            <div className="mt-2">
                                <Message severity="info" text={pdfPasswordHint} />
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </div>
    );
};

export default PAN2OutputComponent;
